// src/components/Pages/ManageBriefsPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Loader,
    Stack,
    Alert,
    Group,
    Accordion,
    Modal,
    Button,
    TextInput,
    Textarea,
    Select,
    Pagination,
    Badge,
    Switch,
    Space,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUsersGroup, IconAlertCircle, IconCheck, IconX, IconTag, IconWorld } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { DateTimePicker } from '@mantine/dates'; // <<< ADDED from new package
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import BriefDisplay from '../Briefs/BriefDisplay';

// Firebase Imports
import { getFirestore, collection, query, where, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore'; // <<< ADDED doc, updateDoc
import { useAuth } from '../../context/AuthContext';

const statusOptions = ['Pending', 'Dispatched', 'Draft Received', 'Uploaded to CMS'];
const brandColorMap = {
    'LoveMoney': 'rgb(174, 26, 18)',
    'LoveProperty': 'rgb(82, 110, 167)',
    'LoveFood': 'rgb(223, 82, 135)',
    'LoveExploring': 'rgb(5, 146, 159)',
};


function ManageBriefsPage() {
    const contentRef = useRef(null);
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const { currentUser, userProfile } = useAuth();

    console.log('ManageBriefsPage -- isBackgroundImageVisible:', isBackgroundImageVisible);


    // Data states
    const [briefs, setBriefs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
    const [selectedBriefForModal, setSelectedBriefForModal] = useState(null);

    // --- NEW: State for editing a brief ---
    const [activeAccordionItem, setActiveAccordionItem] = useState(null);
    const [editingWriter, setEditingWriter] = useState('');
    const [editingDeadline, setEditingDeadline] = useState(null);
    const [editingNotes, setEditingNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [activePage, setPage] = useState(1);
    const [viewMode, setViewMode] = useState('personal'); // 'personal' or 'all'

    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;


    const getBrandColor = (brand) => {
        // Looks up the brand in our map. If not found, it returns the custom purple.
        return brandColorMap[brand] || 'rgb(122, 59, 91)';
    };

    // --- NEW: Function to handle expanding an accordion item ---
    // When an item is opened, we populate the editing state with its current data
    const handleAccordionChange = (value) => {
        setActiveAccordionItem(value);
        if (!value) return; // Exit if no item is selected

        const currentBrief = briefs.find(b => b.id === value);
        if (currentBrief) {
            setEditingWriter(currentBrief.assignedWriter || '');
            setEditingNotes(currentBrief.notes || '');

            // --- NEW ROBUST DATE HANDLING ---
            const deadlineValue = currentBrief.deadline;

            // This is a diagnostic log to help us see what's happening.
            console.log(`Processing deadline for brief ${currentBrief.id}. Type: ${typeof deadlineValue}, Value:`, deadlineValue);

            if (deadlineValue && typeof deadlineValue.toDate === 'function') {
                // Case 1: It's a Firestore Timestamp. Convert it to a JS Date.
                setEditingDeadline(deadlineValue.toDate());
            } else if (deadlineValue) {
                // Case 2: It's NOT a Timestamp (e.g., a string from Firestore or a Date from local state).
                // We can safely convert it to a JS Date object.
                setEditingDeadline(new Date(deadlineValue));
            } else {
                // Case 3: It's null or undefined. Default to null.
                setEditingDeadline(null);
            }
        }
    };

    // --- NEW: Function to save changes to Firestore ---
    const handleSaveChanges = async (briefId) => {
        setIsSaving(true);
        const db = getFirestore();
        const briefRef = doc(db, 'generated_briefs', briefId);

        try {
            await updateDoc(briefRef, {
                assignedWriter: editingWriter,
                deadline: editingDeadline,
                notes: editingNotes
            });

            // Show success notification
            notifications.show({
                title: 'Brief Updated',
                message: 'Your changes have been saved successfully.',
                color: 'teal',
                icon: <IconCheck size={18} />,
            });

            setBriefs(briefs.map(b => b.id === briefId ? { ...b, assignedWriter: editingWriter, deadline: editingDeadline, notes: editingNotes } : b));

        } catch (err) {
            console.error("Error updating document: ", err);
            // Show error notification
            notifications.show({
                title: 'Error',
                message: 'Failed to save your changes. Please try again.',
                color: 'red',
                icon: <IconX size={18} />,
            });
        } finally {
            setIsSaving(false);
        }
    };
    const handleArchive = async (briefId) => {
        setIsArchiving(true);
        const db = getFirestore();
        const briefRef = doc(db, 'generated_briefs', briefId);

        try {
            await updateDoc(briefRef, {
                isArchived: true
            });

            // Show success notification
            notifications.show({
                title: 'Brief Archived',
                message: 'The brief has been moved to the archive.',
                color: 'blue',
                icon: <IconCheck size={18} />,
            });

            setBriefs(briefs.filter(b => b.id !== briefId));
        } catch (err) {
            console.error("Error archiving document: ", err);
            // Show error notification
            notifications.show({
                title: 'Error',
                message: 'Failed to archive the brief. Please try again.',
                color: 'red',
                icon: <IconX size={18} />,
            });
        } finally {
            setIsArchiving(false);
        }
    };
    const handleStatusChange = async (briefId, newStatus) => {
        // --- FIX: Add a guard to prevent processing null values ---
        if (newStatus === null || newStatus === undefined) {
            console.log("Status change to null was blocked.");
            return; // Exit the function immediately if the new status is null
        }

        // We'll optimistically update the UI first for a snappy feel
        const originalBriefs = briefs;
        const updatedBriefs = briefs.map(b =>
            b.id === briefId ? { ...b, status: newStatus } : b
        );
        setBriefs(updatedBriefs);

        const db = getFirestore();
        const briefRef = doc(db, 'generated_briefs', briefId);

        try {
            await updateDoc(briefRef, {
                status: newStatus
            });
            notifications.show({
                title: 'Status Updated',
                message: `Brief status set to "${newStatus}".`,
                color: 'green',
                icon: <IconCheck size={18} />,
            });
        } catch (err) {
            console.error("Error updating status: ", err);
            // If the save fails, revert the UI change and show an error
            setBriefs(originalBriefs);
            notifications.show({
                title: 'Error',
                message: 'Failed to update the status. Please try again.',
                color: 'red',
                icon: <IconX size={18} />,
            });
        }
    };
    useEffect(() => {
        const fetchBriefs = async () => {
            if (!currentUser) {
                setIsLoading(false);
                return;
            }
            setIsLoading(true); // Set loading true at the start of fetch
            try {
                const db = getFirestore();
                const briefsCollection = collection(db, 'generated_briefs');
                let q; // Declare query variable

                // --- NEW: DYNAMIC QUERY LOGIC ---
                if (viewMode === 'all' && userProfile?.role === 'admin') {
                    // Admin 'all' view: fetch all non-archived briefs
                    console.log("Fetching ALL briefs for admin view...");
                    q = query(
                        briefsCollection,
                        where('creationTimestamp', '>=', new Date('2025-06-01')),
                        orderBy('creationTimestamp', 'desc')
                    );
                } else {
                    // Personal view (for everyone, including admin by default)
                    console.log("Fetching PERSONAL briefs...");
                    q = query(
                        briefsCollection,
                        where('uid', '==', currentUser.uid),
                        where('creationTimestamp', '>=', new Date('2025-06-01')),
                        orderBy('creationTimestamp', 'desc')
                    );
                }

                const querySnapshot = await getDocs(q);
                const initialData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Client-side filter for archived briefs remains the same
                const filteredData = initialData.filter(brief => brief.isArchived !== true);

                setBriefs(filteredData);

            } catch (err) {
                console.error("Error fetching briefs:", err);
                setError("Failed to fetch briefs. If switching views, you may need a new Firestore Index.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchBriefs();
    }, [currentUser, userProfile, viewMode]); // <<< Add userProfile and viewMode to dependency array

    useEffect(() => {
        // When the page number changes, scroll the content area into view.
        if (contentRef.current) {
            contentRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [activePage]);

    const renderContent = () => {
        if (isLoading) { return (<Group justify="center" mt="xl"><Loader /><Text>Loading your briefs...</Text></Group>); }
        if (error) { return (<Alert title="Error" color="red" icon={<IconAlertCircle />}>{error}</Alert>); }
        if (briefs.length === 0) { return <Text>No briefs found. Go create one!</Text>; }

        // --- NEW: PAGINATION LOGIC ---
        const BRIEFS_PER_PAGE = 8; // Show 10 briefs per page
        const totalPages = Math.ceil(briefs.length / BRIEFS_PER_PAGE);

        // Calculate the start and end index for the current page
        const startIndex = (activePage - 1) * BRIEFS_PER_PAGE;
        const endIndex = startIndex + BRIEFS_PER_PAGE;

        // "Slice" the array to get only the briefs for the current page
        const briefsToShow = briefs.slice(startIndex, endIndex);

        return (
            <> {/* Use a Fragment to wrap multiple components */}
                <Accordion variant="separated" radius="md" value={activeAccordionItem} onChange={handleAccordionChange}>
                    {briefsToShow.map(brief => ( // <<< MODIFIED: We now map over `briefsToShow`
                        <Accordion.Item value={brief.id} key={brief.id}>
                            {/* The content of your Accordion.Item (Control and Panel)
                          remains exactly the same as before. No changes needed inside here.
                        */}
                            <Accordion.Control>
                                <Stack gap="xs">
                                    <Text size="md" fw={500}>{brief.generatedBrief?.titleSuggestions?.writerHubAlternatives[0] || 'Untitled Brief'}</Text>
                                    <Text size="xs" c="dimmed">
                                        Created: {brief.creationTimestamp ? brief.creationTimestamp.toDate().toLocaleString() : 'Date not available'}

                                        {/* Always show the 'From' text */}
                                        {` | From: "${brief.requestInputs?.raw_title_idea}"`}

                                        {/* --- NEW: Conditionally ADD the author's email for the admin view --- */}
                                        {viewMode === 'all' && ` | Commissioner: ${brief.userEmail || 'N/A'}`}
                                    </Text>
                                    <Group gap="xs" mt="xs">

                                        {brief.requestInputs?.target_brand && (
                                            <Badge
                                                size="sm"
                                                variant="filled"
                                                //leftSection={<IconTag size={14} />}
                                                color={getBrandColor(brief.requestInputs.target_brand)}
                                                styles={{
                                                    label: {
                                                        fontWeight: 400,      // Sets font-weight to normal (un-emboldened)
                                                        //color: 'white',       // You can force it to white, black, or any color.
                                                        // For dark mode, you might want a lighter gray from the theme:
                                                        color: isDark ? theme.colors.gray[2] : theme.colors.gray[2],
                                                    },
                                                }}
                                            >
                                                {brief.requestInputs.target_brand}
                                            </Badge>
                                        )}

                                        {brief.requestInputs?.target_region && (
                                            <Badge
                                                size="sm"
                                                variant="light"
                                                color="gray.5"
                                                leftSection={<IconWorld size={14} />}
                                                styles={{
                                                    label: {
                                                        fontWeight: 400,      // Sets font-weight to normal (un-emboldened)
                                                        //color: 'white',       // You can force it to white, black, or any color.
                                                        // For dark mode, you might want a lighter gray from the theme:
                                                        color: isDark ? theme.colors.gray[4] : theme.colors.gray[9],

                                                    },
                                                }}
                                            >
                                                {brief.requestInputs.target_region}
                                            </Badge>
                                        )}

                                        {(brief.status && brief.status !== 'Pending') && (
                                            <Badge
                                                size="sm"
                                                variant="filled"
                                                color="black"
                                                styles={{
                                                    label: {
                                                        fontWeight: 700,
                                                        color: isDark ? theme.colors.white : theme.colors.white,
                                                    },
                                                }}
                                            >
                                                {brief.status}
                                            </Badge>
                                        )}
                                    </Group>
                                </Stack>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack>
                                    <Group >
                                        <Button
                                            variant="light"
                                            //color="yellow"
                                            size="sm" // A smaller size looks good here
                                            onClick={() => {
                                                setSelectedBriefForModal(brief.generatedBrief);
                                                openModal();
                                            }}
                                        >
                                            View Full Brief
                                        </Button>
                                    </Group>
                                    <TextInput
                                        label="Assign to Writer"
                                        placeholder="Enter writer's name"
                                        value={editingWriter}
                                        onChange={(event) => setEditingWriter(event.currentTarget.value)}
                                    />
                                    <DateTimePicker
                                        label="Set Deadline"
                                        placeholder="Pick date and time"
                                        value={editingDeadline}
                                        onChange={setEditingDeadline}
                                        clearable
                                    />
                                    <Select
                                        label="Status"
                                        data={['Pending', 'Dispatched', 'Draft Received', 'Draft Approved', 'Uploaded to CMS']}
                                        value={brief.status || 'Pending'}
                                        onChange={(value) => handleStatusChange(brief.id, value)}
                                    />
                                    <Textarea
                                        label="Notes"
                                        placeholder="Add any relevant notes for this brief..."
                                        value={editingNotes}
                                        onChange={(event) => setEditingNotes(event.currentTarget.value)}
                                        autosize
                                        minRows={3}
                                    />
                                    <Group justify="space-between" mt="md">
                                        <Button color="red" variant="outline" onClick={() => handleArchive(brief.id)} loading={isArchiving}>Archive</Button>
                                        <Button onClick={() => handleSaveChanges(brief.id)} loading={isSaving}>Save Changes</Button>
                                    </Group>
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>

                {/* --- NEW: PAGINATION COMPONENT --- */}
                <Group justify="center" mt="xl">
                    <Pagination total={totalPages} value={activePage} onChange={setPage} />
                </Group>
            </>
        );
    };

    return (
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                overflow: 'auto',
            }}
        >
            <Container size="lg" py="xl" style={{ width: '100%', flexGrow: 1 }}>
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconUsersGroup color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                    Manage Briefs
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Review, assign, and track the status of your generated content briefs.
                </Text>



                <Paper ref={contentRef} withBorder p="xl" radius="md" shadow="md">
                    {renderContent()}
                </Paper>

                <Modal
                    opened={modalOpened}
                    onClose={closeModal}
                    title="Brief Details"
                    size="xl"
                    scrollAreaComponent={Modal.NativeScrollArea}
                >
                    {selectedBriefForModal && (
                        <BriefDisplay brief={selectedBriefForModal} />
                    )}
                </Modal>
            </Container>
            {/* --- NEW: ADMIN TOGGLE --- */}
            {userProfile && userProfile.role === 'admin' && (
                <Group justify="center" mb="md">

                    <Switch
                        checked={viewMode === 'all'}
                        onChange={(event) => setViewMode(event.currentTarget.checked ? 'all' : 'personal')}
                        label=""
                        size="md"
                    />
                </Group>
            )}
        </Box>
    );
}

export default ManageBriefsPage;